/**
 * Derives whether the user should see "active subscriber" UI (manage / cancel)
 * vs "no paid access — upgrade / resubscribe" using getSubscriptions-style payloads.
 */

import {
	isExemptProductName,
	isInternalProductName,
	isKnownExemptStripeProductId,
} from './exemptProductIds';

const TERMINAL = new Set([
	'expired',
	'cancelled',
	'canceled',
	'inactive',
	'incomplete_expired',
	'unpaid',
]);

const ACTIVE_LIKE = new Set(['active', 'subscribed', 'cancelling']);

/** Status values that should show “Expired” (or equivalent) in plan UI — not the same as full TERMINAL (cancelled is separate). */
const EXPIRED_LIKE_FOR_UI = new Set(['expired', 'inactive', 'incomplete_expired', 'unpaid']);

const DAY_MS = 86400000;

function normStatus(s: unknown): string {
	return typeof s === 'string' ? s.trim().toLowerCase() : '';
}

function pushRow(rows: Record<string, unknown>[], o: unknown) {
	if (o && typeof o === 'object' && !Array.isArray(o)) {
		rows.push(o as Record<string, unknown>);
	}
}

function collectRows(payload: Record<string, unknown>): Record<string, unknown>[] {
	const rows: Record<string, unknown>[] = [];
	pushRow(rows, payload);
	const nested = payload.data as { data?: unknown } | unknown;
	const inner =
		nested && typeof nested === 'object' && nested !== null && 'data' in nested
			? (nested as { data: unknown }).data
			: nested;
	if (Array.isArray(inner)) {
		inner.forEach((item) => pushRow(rows, item));
	} else {
		pushRow(rows, inner);
	}
	const itemsData = (payload.items as { data?: unknown[] } | undefined)?.data;
	const line0 = Array.isArray(itemsData) ? itemsData[0] : undefined;
	pushRow(rows, line0);
	return rows;
}

function trialEndToMillis(raw: unknown): number | null {
	if (raw == null || raw === '') return null;
	const n =
		typeof raw === 'string' ? Number(raw) : typeof raw === 'number' ? raw : NaN;
	if (!Number.isFinite(n) || n <= 0) return null;
	return n > 1_000_000_000_000 ? n : n * 1000;
}

/** Backend billing_info stores `trialEnd` as ISO; Stripe payloads use `trial_end` (unix). */
function trialEndIsoToMillis(raw: unknown): number | null {
	if (raw == null || raw === '') return null;
	if (typeof raw === 'string') {
		const t = Date.parse(raw);
		return Number.isFinite(t) ? t : null;
	}
	if (raw instanceof Date) {
		const t = raw.getTime();
		return Number.isFinite(t) ? t : null;
	}
	return null;
}

function rowTrialEndMillis(row: Record<string, unknown>): number | null {
	const md = row.metadata;
	const meta =
		md && typeof md === 'object' && !Array.isArray(md)
			? (md as Record<string, unknown>)
			: null;
	return (
		trialEndToMillis(row.trial_end) ??
		trialEndIsoToMillis(row.trialEnd) ??
		(meta ? trialEndIsoToMillis(meta.trialEnd) : null)
	);
}

/**
 * True when the user completed at least one paid billing period (not trial-only).
 * past_due grace applies only in this case.
 */
export function deriveHadPaidSubscriptionBeforeRow(
	row: Record<string, unknown>,
): boolean {
	const md = row.metadata;
	const meta =
		md && typeof md === 'object' && !Array.isArray(md)
			? (md as Record<string, unknown>)
			: null;

	if (meta?.hadCompletedPaidPeriod === true || meta?.hadPaidSubscription === true) {
		return true;
	}

	const trialEndMs = rowTrialEndMillis(row);
	if (trialEndMs == null) {
		return true;
	}

	if (trialEndMs > Date.now()) {
		return false;
	}

	const periodStartMs = trialEndToMillis(row.current_period_start);
	if (periodStartMs != null && periodStartMs > trialEndMs + DAY_MS) {
		return true;
	}

	return false;
}

function subscriptionRowIndicatesTrial(row: Record<string, unknown>): boolean {
	const trialEndMs = rowTrialEndMillis(row);
	if (trialEndMs != null && trialEndMs > Date.now()) {
		return true;
	}

	if (normStatus(row.status) === 'trialing') {
		return trialEndMs == null || trialEndMs > Date.now();
	}

	const md = row.metadata;
	if (md && typeof md === 'object' && !Array.isArray(md)) {
		const meta = md as Record<string, unknown>;
		if (normStatus(meta.stripeSubscriptionStatus) === 'trialing') {
			const msMeta = trialEndIsoToMillis(meta.trialEnd);
			return msMeta == null || msMeta > Date.now();
		}
	}

	return false;
}

function rowStatusGrantsEntitlement(row: Record<string, unknown>, st: string): boolean {
	if (!st || TERMINAL.has(st)) {
		return false;
	}

	if (st === 'trialing') {
		return subscriptionRowIndicatesTrial(row);
	}

	if (st === 'past_due') {
		return deriveHadPaidSubscriptionBeforeRow(row);
	}

	return ACTIVE_LIKE.has(st);
}

function rowGrantsEntitlement(row: Record<string, unknown>): boolean {
	const st = normStatus(row.status);

	if (st) {
		return rowStatusGrantsEntitlement(row, st);
	}

	if (row.isActive === false) {
		return false;
	}

	if (row.isActive === true) {
		return true;
	}

	return false;
}

export type SubscriptionEntitlementInput = {
	subscriptionPayload: unknown;
	isInTrial: boolean;
	/** EXEMPT_PRODUCT keeps access even when status is expired (matches PlanComponent.web). */
	isExemptProduct?: boolean;
};

export function hasActivePaidEntitlementFromPayload(
	input: SubscriptionEntitlementInput
): boolean {
	const { subscriptionPayload, isInTrial, isExemptProduct } = input;
	if (isExemptProduct) return true;
	if (isInTrial) return true;
	if (subscriptionPayload == null) return false;
	if (typeof subscriptionPayload === 'string') return false;

	const plan = subscriptionPayload as Record<string, unknown>;
	if (isExemptProduct && normStatus(plan.status) === 'expired') {
		return true;
	}

	const rows = collectRows(plan);
	let anyEntitled = false;
	let anyTerminal = false;

	for (const row of rows) {
		const st = normStatus(row.status);

		if (st && TERMINAL.has(st)) {
			anyTerminal = true;
			continue;
		}

		if (rowGrantsEntitlement(row)) {
			anyEntitled = true;
			continue;
		}

		if (row.isActive === false || st === 'past_due' || st === 'trialing') {
			anyTerminal = true;
		}
	}

	if (anyEntitled) return true;
	if (anyTerminal) return false;
	if (plan.isActive === false) return false;

	const prod = plan.product;
	const hasProductHint =
		prod != null &&
		prod !== 'no_product' &&
		(typeof prod === 'string' ? prod.length > 0 : typeof prod === 'object');

	return Boolean(hasProductHint);
}

/**
 * True when payload explicitly carries an expired-like subscription status (root or nested row).
 * EXEMPT_PRODUCT + root `expired` stays false so UI matches “still entitled” behavior.
 */
export function subscriptionPayloadHasExpiredLikeStatus(
	payload: unknown,
	isExemptProduct: boolean,
): boolean {
	if (!payload || typeof payload !== 'object') return false;
	if (isExemptProduct) return false;
	const plan = payload as Record<string, unknown>;
	for (const row of collectRows(plan)) {
		const st = normStatus(row.status);
		if (EXPIRED_LIKE_FOR_UI.has(st)) {
			return true;
		}
		if (st === 'past_due' && !deriveHadPaidSubscriptionBeforeRow(row)) {
			return true;
		}
		if (st === 'trialing' && !subscriptionRowIndicatesTrial(row)) {
			return true;
		}
	}
	const rootSt = normStatus(plan.status);
	if (EXPIRED_LIKE_FOR_UI.has(rootSt)) {
		return true;
	}
	return false;
}

/** Same row sweep as PlanComponent (root + nested Stripe / line item). */
export function deriveIsInTrialFromSubscriptionPayload(payload: unknown): boolean {
	if (!payload || typeof payload !== 'object') return false;
	const rows = collectRows(payload as Record<string, unknown>);
	return rows.some(subscriptionRowIndicatesTrial);
}

export function deriveIsInternalPlanFromSubscriptionPayload(payload: unknown): boolean {
	if (!payload || typeof payload !== 'object') return false;
	const plan = payload as Record<string, unknown>;
	if (plan.isInternal === true) return true;
	const name = plan.productName ?? plan.product;
	return isInternalProductName(typeof name === 'string' ? name : undefined);
}

/** Matches PlanComponent.web EXEMPT_PRODUCT detection across nested shapes. */
export function deriveIsExemptProductFromSubscriptionPayload(
	payload: unknown,
): boolean {
	if (!payload || typeof payload !== 'object') return false;
	if (deriveIsInternalPlanFromSubscriptionPayload(payload)) return false;
	const plan = payload as Record<string, unknown>;
	if (plan.isExempt === true) return true;
	const nestedData = (plan.data as { data?: unknown } | undefined)?.data ?? plan.data;
	let normalizedPlan: Record<string, unknown> | null = null;
	if (Array.isArray(nestedData) && nestedData[0] && typeof nestedData[0] === 'object') {
		normalizedPlan = nestedData[0] as Record<string, unknown>;
	} else if (nestedData && typeof nestedData === 'object' && !Array.isArray(nestedData)) {
		normalizedPlan = nestedData as Record<string, unknown>;
	}

	const productField = plan.product;
	const productId =
		(plan as { productId?: unknown }).productId ??
		(plan as { productDetails?: { id?: unknown } }).productDetails?.id ??
		(typeof productField === 'object' && productField !== null
			? (productField as { id?: unknown }).id
			: undefined) ??
		(normalizedPlan?.productId as unknown) ??
		(typeof normalizedPlan?.product === 'object' &&
		normalizedPlan?.product !== null
			? (normalizedPlan.product as { id?: unknown }).id
			: undefined);
	const productName =
		(plan as { productName?: unknown }).productName ??
		(typeof productField === 'string'
			? productField
			: productField && typeof productField === 'object' && productField !== null
				? (productField as { name?: string }).name
				: undefined) ??
		(normalizedPlan?.plan as { product?: string } | undefined)?.product ??
		(typeof normalizedPlan?.product === 'string'
			? normalizedPlan.product
			: (normalizedPlan?.product as { name?: string } | undefined)?.name) ??
		(plan as { productDetails?: { name?: string } }).productDetails?.name;

	const lowerProductName = typeof productName === 'string' ? productName.toLowerCase() : '';
	return (
		isKnownExemptStripeProductId(productId) ||
		isExemptProductName(productName) ||
		productName === 'EXEMPT_PRODUCT' ||
		lowerProductName.includes('exempt')
	);
}

/**
 * Single entitlement check from raw `getSubscriptions()` payload — same rules as PlanComponent cards.
 */
export function hasActivePaidEntitlementFromSubscriptionPayload(
	payload: unknown,
): boolean {
	if (payload == null || typeof payload === 'string') return false;
	return hasActivePaidEntitlementFromPayload({
		subscriptionPayload: payload,
		isInTrial: deriveIsInTrialFromSubscriptionPayload(payload),
		isExemptProduct:
			deriveIsExemptProductFromSubscriptionPayload(payload)
			|| deriveIsInternalPlanFromSubscriptionPayload(payload),
	});
}
